/**
 * Ficha do cliente da Central no desenho da ficha de Clientes do Legado:
 * cabeçalho fixo, coluna de contexto à esquerda e os pedidos (cartões que
 * abrem) à direita. As peças do Legado são reaproveitadas daqui; o que é só
 * da Central (leitura da IA, linhas de ambiente, abas) fica neste arquivo.
 * Cor só no estado (leitura da IA, atraso); o resto neutro.
 */
import styled, { css } from "styled-components";

import { BP_MOBILE } from "../../../styles/canonical/breakpoints";
import {
  HeaderActions,
  HeaderBackButton,
  HeaderBarRow,
  KpiLabel,
  StickyHeaderSlot,
} from "../../Clientes/ClienteDetalhes/styles";
import { PedidoTitle } from "../../Clientes/styles";

export {
  ClearLink,
  ContextSticky,
  DateList,
  DateRow,
  DetailRoot,
  HeaderDot,
  HeaderIdentity,
  HeaderName,
  HeaderPill,
  HeaderPills,
  KpiCard,
  KpiGrid,
  KpiLabel,
  KpiValue,
  MainColumn,
  PanelToolbar,
  PedidoNavItem,
  PedidoNavList,
  RailCard,
  RailTitle,
  SearchBox,
  SearchIcon,
  SearchInput,
  StatusChip,
  StatusChipsRow,
  ThreeZoneGrid,
  ToolbarMeta,
} from "../../Clientes/ClienteDetalhes/styles";
export {
  Chip,
  ChipRow,
  EmptyState,
  PedidoBody,
  PedidoCard,
  PedidoContent,
  PedidoList,
  PedidoMeta,
  PedidoToggleButton,
  PedidoToggleIcon,
} from "../../Clientes/styles";

const tom = (nome, parte) => `var(--${nome || "neutral"}-${parte})`;
const CELULAR = `(max-width: ${BP_MOBILE - 8}px)`;

/* O cabeçalho do Legado gruda no topo da página; aqui a página rola sob o
   cabeçalho do app, então ele gruda logo abaixo dele. */
export const CabecalhoFixo = styled(StickyHeaderSlot)`
  top: var(--app-header-height, 0px);
`;

/* Celular: voltar (só o ícone) e o nome na mesma linha; as ações embaixo, compactas. */
export const LinhaDoCabecalho = styled(HeaderBarRow)`
  @media ${CELULAR} {
    grid-template-columns: auto minmax(0, 1fr);
    gap: 8px 10px;
    padding: 10px 14px;
  }
`;

export const BotaoVoltar = styled(HeaderBackButton)`
  white-space: nowrap;

  @media ${CELULAR} {
    align-self: start;
    width: 34px;
    height: 34px;
    padding: 0;
    justify-content: center;

    span {
      display: none;
    }
  }
`;

export const AcoesDoCabecalho = styled(HeaderActions)`
  flex-wrap: wrap;
  justify-content: flex-end;

  > * {
    white-space: nowrap;
  }

  @media ${CELULAR} {
    grid-column: 1 / -1;
    justify-self: stretch;
    justify-content: flex-start;

    /* Botões só com o ícone (o nome fica no aria-label). */
    .rotulo-longo {
      display: none;
    }

    button:has(.rotulo-longo) {
      padding: 0 10px;
    }
  }
`;

/* O título do cartão do pedido sem a margem padrão do h4 (o Legado tem um reset que aqui não vale). */
export const TituloDoPedido = styled(PedidoTitle)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 0;
  margin: 0;
  line-height: 1.25;
`;

/* Rótulo do indicador sem caixa alta (mais curto e mais fácil de ler que o do Legado). */
export const RotuloDoIndicador = styled(KpiLabel)`
  font-size: 12px;
  line-height: 1.3;
  letter-spacing: 0;
  text-transform: none;
`;

/* ---------- Leitura da IA (coluna de contexto) ---------- */

export const LeituraDaIA = styled.section`
  display: grid;
  gap: 8px;
  padding: 14px 14px 12px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);

  > header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-3);
  }

  > p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: var(--ink);
    text-wrap: pretty;
  }

  > small {
    font-size: 12px;
    color: var(--ink-3);
  }
`;

export const EstadoComPonto = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-2);

  &::before {
    content: "";
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $tom }) => tom($tom, "solid")};
  }
`;

/* No celular a lista de pedidos da coluna sai: os cartões estão logo abaixo. */
export const SoNoComputador = styled.div`
  @media ${CELULAR} {
    display: none;
  }
`;

/* ---------- Abas ---------- */

const AbasBase = styled.div`
  display: flex;
  gap: 2px;
  min-width: 0;
  border-bottom: 1px solid var(--border);
  overflow: hidden;
`;

export const Aba = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 1 auto;
  min-width: 0;
  height: 42px;
  padding: 0 12px;
  border: 0;
  border-bottom: 2px solid ${({ $ativa }) => ($ativa ? "var(--ink)" : "transparent")};
  margin-bottom: -1px;
  background: transparent;
  color: ${({ $ativa }) => ($ativa ? "var(--ink)" : "var(--ink-3)")};
  font: inherit;
  font-size: 14px;
  font-weight: ${({ $ativa }) => ($ativa ? 600 : 500)};
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: var(--ink);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }

  span {
    min-width: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: var(--surface-2);
    font-size: 12px;
    font-weight: 600;
    line-height: 20px;
    text-align: center;
    color: var(--ink-2);
    font-variant-numeric: tabular-nums;
  }

  > b {
    min-width: 0;
    font-weight: inherit;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    flex: 0 0 auto;
  }

  /* Celular: as quatro abas cabem numa linha; a contagem vira texto simples. */
  @media ${CELULAR} {
    gap: 4px;
    padding: 0 6px;
    font-size: 13px;

    span {
      min-width: 0;
      padding: 0;
      background: transparent;
      font-size: 12px;
      line-height: inherit;
      color: inherit;
      opacity: 0.75;
    }
  }
`;

/* Com a aba Perfil são cinco: no celular a contagem sai da aba (cada parte
   repete o número no próprio título) para os nomes caberem inteiros. */
export const Abas = styled(AbasBase)`
  ${({ $cinco }) =>
    $cinco &&
    css`
      @media ${CELULAR} {
        justify-content: space-between;

        ${Aba} {
          padding: 0 4px;
          font-size: 12.5px;
        }

        ${Aba} span {
          display: none;
        }
      }
    `}
`;

export const PainelDaAba = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;

/* ---------- Ambientes dentro do pedido ---------- */

const COLUNAS = "minmax(0, 1.2fr) minmax(0, 0.9fr) minmax(0, 2.2fr) minmax(0, 1.05fr) minmax(0, 0.85fr)";

export const TabelaDeAmbientes = styled.div`
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--surface);
  overflow: hidden;
  min-width: 0;
`;

export const CabecalhoDaTabela = styled.div`
  display: grid;
  grid-template-columns: ${COLUNAS};
  gap: 12px;
  padding: 8px 14px;
  background: var(--surface-2);
  border-bottom: 1px solid var(--border);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-3);

  @media ${CELULAR} {
    display: none;
  }
`;

export const LinhasDeAmbiente = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  > li + li {
    border-top: 1px solid var(--divider);
  }
`;

export const LinhaDoAmbiente = styled.div`
  display: grid;
  grid-template-columns: ${COLUNAS};
  gap: 12px;
  align-items: start;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.45;
  color: var(--ink-2);

  @media ${CELULAR} {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "nome etapa"
      "ia ia"
      "meta meta";
    gap: 6px 10px;

    > .amb-nome { grid-area: nome; }
    > .amb-etapa { grid-area: etapa; text-align: right; }
    > .amb-ia { grid-area: ia; }
    > .amb-prazo,
    > .amb-projetista { display: none; }
    > .amb-meta { grid-area: meta; display: block; }
  }
`;

export const NomeDoAmbiente = styled.div.attrs({ className: "amb-nome" })`
  display: grid;
  gap: 2px;
  min-width: 0;

  b {
    font-size: 14px;
    font-weight: 600;
    color: var(--ink);
    overflow-wrap: anywhere;
  }
`;

export const AbrirLigados = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  justify-self: start;
  padding: 2px 0;
  border: 0;
  background: transparent;
  color: var(--ink-3);
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: var(--ink);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
    border-radius: 4px;
  }

  svg {
    transition: transform 160ms cubic-bezier(0.23, 1, 0.32, 1);
    transform: rotate(${({ $aberto }) => ($aberto ? "180deg" : "0deg")});
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }
  }
`;

export const Celula = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;

  small {
    display: block;
    font-size: 12px;
    color: var(--ink-3);
  }

  ${({ $tom }) =>
    $tom &&
    css`
      strong {
        color: ${tom($tom, "text")};
        font-weight: 600;
        white-space: nowrap;
      }
    `}
`;

/* Só no celular: prazo e projetista numa linha embaixo. */
export const MetaNoCelular = styled.div.attrs({ className: "amb-meta" })`
  display: none;
  font-size: 12.5px;
  color: var(--ink-3);

  strong {
    font-weight: 600;
    color: ${({ $tom }) => ($tom ? tom($tom, "text") : "inherit")};
  }
`;

export const SeloDoEstado = styled.span`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  margin-bottom: 4px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ $tom }) => tom($tom, "bg")};
  color: ${({ $tom }) => tom($tom, "text")};
`;

export const SituacaoDaIA = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: ${({ $vazia }) => ($vazia ? "var(--ink-4)" : "var(--ink-2)")};
  text-wrap: pretty;
`;

/* Pendências e decisões do ambiente, abertas embaixo da linha. */
export const Ligados = styled.div`
  display: grid;
  gap: 12px;
  margin: 0 14px 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--surface-2);

  h4 {
    margin: 0 0 4px;
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-3);
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 6px;
  }

  li {
    font-size: 13px;
    line-height: 1.45;
    color: var(--ink);
  }

  li small {
    display: block;
    font-size: 12px;
    color: var(--ink-3);
  }
`;

export const MarcaDoPedido = styled.span`
  display: inline-flex;
  align-items: center;
  height: 22px;
  margin-left: 8px;
  padding: 0 8px;
  border-radius: 6px;
  background: var(--surface-2);
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-2);
  vertical-align: 3px;
`;

export const AtrasoNoMeta = styled.span`
  color: var(--danger-text);
  font-weight: 600;
`;
